import React, { useEffect, useMemo, useState } from 'react';
import { Chain, ChainStep, ChainWithVersions, Pipeline, PipelineVersionInfo } from '../../types';
import { useNavigate, useParams } from 'react-router-dom';
import VisualChainBuilder from '../../components/VisualChainBuilder';
import { apiGet, apiPatch } from '../../utils/apiClient';

export default function ChainEditPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [chain, setChain] = useState<Chain | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [continueAfterFailure, setContinueAfterFailure] = useState(false);
  const [cleanupResourcesOnCompletion, setCleanupResourcesOnCompletion] = useState(false);
  const [steps, setSteps] = useState<ChainStep[]>([]);
  const [stepsText, setStepsText] = useState('');
  const [activeTab, setActiveTab] = useState<'visual' | 'json'>('visual');
  const [pipelineVersions, setPipelineVersions] = useState<Record<string, PipelineVersionInfo>>({});

  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [customContainers, setCustomContainers] = useState<Array<{container_id: string; name: string}>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPipes, setLoadingPipes] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Load chain with version information
  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiGet(`/api/chains/${id}/with-versions`);
        if (!res.ok) throw new Error(`Failed to load chain (${res.status})`);
        const data: ChainWithVersions = await res.json();
        // Set basic chain data
        setChain({
          chain_id: data.chain_id,
          name: data.name,
          description: data.description,
          steps: data.steps,
          continue_after_failure: data.continue_after_failure,
          cleanup_resources_on_completion: data.cleanup_resources_on_completion,
          created_at: data.created_at,
          updated_at: data.updated_at
        });
        setName(data.name);
        setDescription(data.description || '');
        setContinueAfterFailure(data.continue_after_failure || false);
        setCleanupResourcesOnCompletion(data.cleanup_resources_on_completion || false);
        setSteps(data.steps || []);
        setStepsText(JSON.stringify(data.steps || [], null, 2));
        // Set pipeline version information
        setPipelineVersions(data.pipeline_versions || {});
      } catch (e: any) {
        setError(e?.message || 'Failed to load chain');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  // Load pipelines for palette
  useEffect(() => {
    const loadPipes = async () => {
      setLoadingPipes(true);
      try {
        const res = await apiGet('/api/pipelines');
        if (res.ok) {
          const data = await res.json();
          setPipelines(Array.isArray(data) ? data : []);
        }
      } catch {}
      setLoadingPipes(false);
    };
    loadPipes();
  }, []);

  // Load custom containers
  useEffect(() => {
    const loadContainers = async () => {
      try {
        const res = await apiGet('/api/custom-containers/');
        if (res.ok) {
          const data = await res.json();
          const containers = data.containers || [];
          if (Array.isArray(containers)) {
            setCustomContainers(containers.map((c: any) => ({
              container_id: c.container_id,
              name: c.config?.name || c.name || c.container_id
            })));
          }
        }
      } catch {}
    };
    loadContainers();
  }, []);

  // Keep JSON text in sync when switching to JSON tab
  useEffect(() => {
    if (activeTab === 'json') {
      setStepsText(JSON.stringify(steps, null, 2));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const switchTab = (tab: 'visual' | 'json') => {
    if (tab === 'visual' && activeTab === 'json') {
      try {
        const parsed = JSON.parse(stepsText);
        if (Array.isArray(parsed)) setSteps(parsed);
      } catch {}
    }
    setActiveTab(tab);
  };

  const submitPayloadSteps = (): ChainStep[] | null => {
    if (activeTab === 'visual') return steps;
    try {
      const parsed = JSON.parse(stepsText);
      if (!Array.isArray(parsed)) throw new Error('Steps must be an array');
      return parsed;
    } catch (e: any) {
      setError(e?.message || 'Invalid JSON in steps');
      return null;
    }
  };

  const handleSave = async () => {
    if (!id) return;
    setError(null);
    const payloadSteps = submitPayloadSteps();
    if (!payloadSteps) return;
    if (!name.trim()) {
      setError('Name is required');
      return;
    }
    setSaving(true);
    try {
      const res = await apiPatch(`/api/chains/${id}`, { 
        name, 
        description: description || null, 
        steps: payloadSteps, 
        continue_after_failure: continueAfterFailure,
        cleanup_resources_on_completion: cleanupResourcesOnCompletion
      });
      if (!res.ok) throw new Error(`Failed to update chain (${res.status})`);
      const data = await res.json();
      navigate(`/chains/${data.chain_id}`);
    } catch (e: any) {
      setError(e?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const builder = (
    <div style={{ marginTop: 8 }}>
      {loadingPipes ? (
        <div style={{ color: '#6b7280' }}>Loading pipelines…</div>
      ) : (
        <VisualChainBuilder
          pipelines={pipelines}
          value={steps}
          onChange={setSteps}
          readOnly={false}
          customContainers={customContainers}
        />
      )}
    </div>
  );

  const jsonEditor = (
    <label>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>Steps (JSON)</div>
      <textarea
        value={stepsText}
        onChange={e => setStepsText(e.target.value)}
        rows={12}
        spellCheck={false}
        style={{ width: '100%', fontFamily: 'monospace', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}
      />
      <div style={{ color: '#6b7280', fontSize: 12, marginTop: 6 }}>
        Example: [ {`{ "pipeline_id": "A", "depends_on": [] }`}, {`{ "pipeline_id": "B", "depends_on": ["A"] }`} ]
      </div>
    </label>
  );

  const title = useMemo(() => (chain ? `Edit Chain — ${chain.name}` : 'Edit Chain'), [chain]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ margin: 0 }}>{title}</h2>
          <p style={{ color: '#6b7280', marginTop: 6 }}>Update chain name, description, and steps visually or via JSON.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => navigate(-1)} style={{ border: '1px solid #e5e7eb', background: 'white', padding: '10px 14px', borderRadius: 8 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || loading} style={{ background: '#111827', color: 'white', border: 'none', padding: '10px 14px', borderRadius: 8, fontWeight: 700 }}>{saving ? 'Saving…' : 'Save Changes'}</button>
        </div>
      </div>

      {loading && <div style={{ marginTop: 16, color: '#6b7280' }}>Loading…</div>}
      {error && <div style={{ marginTop: 16, color: '#b91c1c', padding: 8, background: '#fee2e2', border: '1px solid #fecaca', borderRadius: 8 }}>{error}</div>}

      {!loading && (
        <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <label>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Name</div>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Chain name" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8 }} />
          </label>
          <label>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>Description</div>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" style={{ width: '100%', padding: '10px 12px', border: '1px solid #e5e7eb', borderRadius: 8 }} />
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={continueAfterFailure} 
              onChange={e => setContinueAfterFailure(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Continue after failure</span>
            <span style={{ color: '#6b7280', fontSize: 13 }}>(Execute remaining steps even if a step fails)</span>
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input 
              type="checkbox" 
              checked={cleanupResourcesOnCompletion} 
              onChange={e => setCleanupResourcesOnCompletion(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            <span>Cleanup resources on completion</span>
            <span style={{ color: '#6b7280', fontSize: 13 }}>(Stop Spark and custom containers when chain finishes)</span>
          </label>

          {/* Tabs */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => switchTab('visual')} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: activeTab === 'visual' ? '#111827' : 'white', color: activeTab === 'visual' ? 'white' : '#374151', fontWeight: 700 }}>Visual Builder</button>
            <button onClick={() => switchTab('json')} style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: activeTab === 'json' ? '#111827' : 'white', color: activeTab === 'json' ? 'white' : '#374151', fontWeight: 700 }}>JSON Editor</button>
          </div>

          {activeTab === 'visual' ? builder : jsonEditor}

          {/* Pipeline Versions Section */}
          {steps.length > 0 && Object.keys(pipelineVersions).length > 0 && (
            <div style={{ marginTop: 16, padding: 16, background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 8 }}>
              <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Pipeline Versions</div>
              <div style={{ display: 'grid', gap: 8 }}>
                {steps.map(step => {
                  const versionInfo = pipelineVersions[step.pipeline_id];
                  const pipeline = pipelines.find(p => p.pipeline_id === step.pipeline_id);
                  const pipelineName = pipeline?.name || step.pipeline_id;
                  
                  if (!versionInfo) return null;
                  
                  return (
                    <div key={step.pipeline_id} style={{ 
                      padding: 12, 
                      background: 'white', 
                      border: '1px solid #e5e7eb', 
                      borderRadius: 6,
                      display: 'grid',
                      gridTemplateColumns: '1fr auto auto',
                      gap: 12,
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>{pipelineName}</div>
                        <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{step.pipeline_id}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ fontSize: 12, color: '#6b7280' }}>Active:</span>
                        <span style={{ 
                          fontSize: 13, 
                          fontWeight: 600, 
                          padding: '4px 8px', 
                          background: '#dcfce7', 
                          color: '#166534',
                          borderRadius: 4
                        }}>
                          {versionInfo.active_version || 'N/A'}
                        </span>
                      </div>
                      {versionInfo.draft_version && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, color: '#6b7280' }}>Draft:</span>
                          <span style={{ 
                            fontSize: 13, 
                            fontWeight: 600, 
                            padding: '4px 8px', 
                            background: '#fef3c7', 
                            color: '#92400e',
                            borderRadius: 4
                          }}>
                            {versionInfo.draft_version}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 12, fontSize: 12, color: '#6b7280', fontStyle: 'italic' }}>
                Note: Chain execution will use the <strong>active version</strong> of each pipeline.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
